/* eslint-disable @tanstack/query/exhaustive-deps -- doesn't work with the key factory*/
import { useQuery } from "@tanstack/react-query";
import { type ClientInferResponseBody } from "@ts-rest/core";
import { type apiContract } from "@kanpla/api-contract";

import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { logger } from "~/services/logger";

import { getCollectOrder } from "../request";
import { collectOrderKeys } from "../keys";

export type UseGetCollectOrderArgs = Partial<{
  childId: string;
  childCardHex: string;
}>;

type CollectOrderResponse = ClientInferResponseBody<
  typeof apiContract.v1.collect.children.getOrders,
  200
>;

export type CollectOrderLine = CollectOrderResponse["orderLines"][number];

type CollectedOrderLine = CollectOrderLine & {
  collectedAt: number;
};

type ActiveOrderLine = CollectOrderLine & {
  collectedAt: null;
};

export type TransformedCollectOrder = Omit<
  CollectOrderResponse,
  "orderLines"
> & {
  collectedOrderLines: CollectedOrderLine[];
  activeOrderLines: ActiveOrderLine[];
};

export const useGetCollectOrder = ({
  childId,
  childCardHex
}: UseGetCollectOrderArgs) => {
  const { data: till } = useCurrentTill();
  const tillId = till?.id;
  return useQuery({
    queryKey: collectOrderKeys.detail({
      childId,
      childCardHex
    }),
    queryFn: () => {
      if (!tillId) {
        logger.error(new Error("Till not found"), "Till not found", {
          tillId
        });
        throw new Error("Till not found");
      }
      if (!childId && !childCardHex) {
        logger.error(
          new Error("Child ID or child card hex is required"),
          "Child ID or child card hex is required",
          {
            childId,
            childCardHex
          }
        );
        throw new Error("Child ID or child card hex is required");
      }
      return getCollectOrder({ childId, childCardHex, tillId });
    },
    select: ({ orderLines, ...rest }): TransformedCollectOrder => {
      return {
        collectedOrderLines: orderLines.filter(isCollected),
        activeOrderLines: orderLines.filter(isActive),
        ...rest
      };
    },
    enabled:
      (Boolean(childId) || Boolean(childCardHex)) &&
      (till?.type === "collect" || till?.type === "collect-lite")
  });
};

function isCollected(
  orderLine: CollectOrderLine
): orderLine is CollectedOrderLine {
  return orderLine.collectedAt !== null;
}

function isActive(orderLine: CollectOrderLine): orderLine is ActiveOrderLine {
  return orderLine.collectedAt === null;
}
